import { HStack, VStack } from "@chakra-ui/layout";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  CloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { RiClipboardLine, RiFilter3Line } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { useFormImportFlow } from "src/components/FormImport/FormImportFlowContext";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import {
  FormTabsTypes,
  SearchAndFilterTypes,
  VisibilityOptions,
} from "src/constants";
import { EnrollmentPeriodIdProvider } from "src/hooks/useGlossary";
import { useFormsList } from "src/scenes/orgAdmin/forms/components/useFormsList";
import * as RD from "src/types/remoteData";
import { FormsList } from "../FormsList";
import { FormListViewWithFilter } from "../components/FormListViewWithFilter";
import { FormFilters } from "../formFilters/FormFilters";
import { FormImportErrorsDialog } from "./FormImportErrorsDialog";

type Props = {
  enrollmentPeriodId: uuid;
  formImportId: uuid;
};

export const ImportedForms = ({
  enrollmentPeriodId,
  formImportId,
}: Props): React.ReactElement | null => {
  const [searchParams] = useSearchParams();
  const { setSelectedEnrollmentPeriodId } = useEnrollmentPeriod();

  useEffect(() => {
    // ensure that the enrollment period for the currentFormImport matches the enrollment period in the window location
    // this prevents users from getting stuck on the ImportedForms component without any visible data when imported form data
    // should be visible
    if (
      searchParams.get(SearchAndFilterTypes.EnrollmentPeriod) !==
      enrollmentPeriodId
    ) {
      setSelectedEnrollmentPeriodId(enrollmentPeriodId);
    }
  }, [searchParams, enrollmentPeriodId, setSelectedEnrollmentPeriodId]);

  const {
    remoteData,
    pagination,
    setPagination,
    selection,
    setSelection,
    search,
    filter,
    formsList,
  } = useFormsList({
    enrollmentPeriodId,
    searchParamDefaultOverrides: {
      [SearchAndFilterTypes.Visibility]: [
        VisibilityOptions.NotVisible,
        VisibilityOptions.Visible,
      ].join("_"),
    },
  });

  const { formImportRowAggregates } = useFormImportFlow();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [showErrors, setShowErrors] = useState(true);

  const dismissError = useCallback(() => {
    setShowErrors(false);
  }, []);

  let alertContent: ReactNode;
  if (
    formImportRowAggregates.hasData() &&
    formImportRowAggregates.data.failed > 0 &&
    showErrors
  ) {
    const errorCount = formImportRowAggregates.data.failed;

    alertContent = (
      <>
        <Alert status="warning" color="orange.900">
          <AlertIcon />

          <AlertDescription flexGrow={1}>
            {errorCount === 1
              ? `${errorCount} error creating forms`
              : `${errorCount} errors creating forms`}

            {" - "}

            <Button
              color="orange.900"
              display="inline-block"
              fontWeight="bold"
              onClick={onOpen}
              textDecoration="underline"
              variant="link"
            >
              See details
            </Button>
          </AlertDescription>

          <CloseButton alignSelf="flex-start" onClick={dismissError} />
        </Alert>

        <FormImportErrorsDialog
          formImportId={formImportId}
          isOpen={isOpen}
          onClose={onClose}
        />
      </>
    );
  }

  const compositeRemoteData = RD.toTuple(
    remoteData.formData,
    remoteData.formTemplateData
  );

  return (
    <EnrollmentPeriodIdProvider enrollmentPeriodId={enrollmentPeriodId}>
      <FormListViewWithFilter searchInput={<SearchInput {...search} />}>
        {({ isFilterOpen, onFilterToggle }) => (
          <VStack align="stretch" minBlockSize="100%">
            <HStack py={6}>
              <Button
                variant="outline"
                leftIcon={<RiFilter3Line />}
                onClick={onFilterToggle}
              >
                Filter
              </Button>
            </HStack>

            <HStack alignItems="start" flexGrow={1}>
              <VStack
                alignSelf="stretch"
                gap={5}
                width={isFilterOpen ? "70%" : "100%"}
              >
                {alertContent}

                <GQLRemoteDataView remoteData={compositeRemoteData}>
                  {([formList, formTemplate]) => {
                    if (!formList.rows.length || !formTemplate) {
                      return (
                        <NoDataTable
                          buttonText="Clear"
                          icon={RiClipboardLine}
                          onButtonAction={formsList.clearAll}
                          text="No forms found"
                        />
                      );
                    } else {
                      return (
                        <FormsList
                          enrollmentPeriodId={enrollmentPeriodId}
                          forms={formList.rows}
                          formTemplate={formTemplate}
                          limit={pagination.limit}
                          offset={pagination.offset}
                          count={formList.totalCount}
                          selection={selection}
                          onSelectionChange={setSelection}
                          onRefetch={formsList.refetch}
                          onFetchAllIds={formsList.fetchAllIds}
                          onFetchMore={setPagination}
                          onSort={formsList.sort}
                          isLoading={compositeRemoteData.isLoading()}
                          onSelectAll={formsList.selectAll}
                          tabType={FormTabsTypes.Submissions}
                          setAttendance={formsList.setAttendance}
                        />
                      );
                    }
                  }}
                </GQLRemoteDataView>
              </VStack>
              <FormFilters
                formTemplateRemoteData={remoteData.formTemplateData}
                onToggleFilter={onFilterToggle}
                onClearFilters={filter.clear}
                enrollmentPeriodId={enrollmentPeriodId}
                display={isFilterOpen ? "flex" : "none"}
                height="calc(100vh - 160px)"
              />
            </HStack>
          </VStack>
        )}
      </FormListViewWithFilter>
    </EnrollmentPeriodIdProvider>
  );
};
